import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import socket from 'websockets/socket';
import { v4 as uuidV4 } from 'uuid';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';

export const useStreamingNotePrompts = ({
  originalNote,
  includeSummary = false,
  includeNote = true,
}) => {
  const dispatch = useAppDispatch();
  const [streamingKey, setStreamingKey] = useState(null);
  const [streamingData, setStreamingData] = useState([]);
  const [loadingNote, setLoadingNote] = useState(false);
  const [newNote, setNewNote] = useState('');

  const socketConnection = useAppSelector((state) => state.resource.socketConnection);

  async function promptNote(streamingKey, fields, patientId) {
    const result = await runSavedPromptTyped('Beautify Note', fields, { patientId, streamingKey });
    return result || '';
  }

  async function promptSummary(streamingKey, fields, patientId) {
    let result = await runSavedPromptTyped('Note Summary', fields, { streamingKey, patientId });
    return result || '';
  }

  function receivedNoteStreaming(data) {
    if (data.streamingKey.startsWith(streamingKey)) {
      setStreamingData((prevState) => [...prevState, data]);
    }
  }

  const filteredStreamingText = useMemo(() => {
    let result = {};
    streamingData.forEach((data) => {
      let key = data.streamingKey.replace(streamingKey, '');
      result[key] = result[key] || '';
      result[key] += data.text;
    });
    return [
      result.Summary,
      result.S,
      result.O,
      result.A,
      result.P,
      includeNote ? undefined : originalNote,
    ]
      .filter((value) => value)
      .join('\n\n');
  }, [streamingData, streamingKey, includeNote, originalNote]);

  useEffect(() => {
    const key = 'ai-streaming';
    let instance = socket.getSocket();
    if (socketConnection === 'connected' && instance) {
      instance.on(key, (data) => {
        receivedNoteStreaming(data);
      });
    }
    return () => {
      if (instance) instance.removeListener(key);
    };
  }, [socketConnection, streamingKey]);

  const generateNote = async (fields, patientId) => {
    const newStreamingKey = uuidV4();
    setStreamingData([]);
    setStreamingKey(newStreamingKey);
    setLoadingNote(true);
    const [summary, s, o, a, p] = await Promise.all([
      includeSummary && promptSummary(`${newStreamingKey}Summary`, fields, patientId),
      includeNote &&
        promptNote(
          `${newStreamingKey}S`,
          {
            ...fields,
            extraPrompt: 'Output only the S section with all the formatting rules stated above',
          },
          patientId,
        ),
      includeNote &&
        promptNote(
          `${newStreamingKey}O`,
          {
            ...fields,
            extraPrompt: 'Output only the O section with all the formatting rules stated above',
          },
          patientId,
        ),
      includeNote &&
        promptNote(
          `${newStreamingKey}A`,
          {
            ...fields,
            extraPrompt: 'Output only the A section with all the formatting rules stated above',
          },
          patientId,
        ),
      includeNote &&
        promptNote(
          `${newStreamingKey}P`,
          {
            ...fields,
            extraPrompt: 'Output only the P section with all the formatting rules stated above',
          },
          patientId,
        ),
    ]);

    let result = [summary, s, o, a, p].filter((v) => v).join('\n\n');
    if (!includeNote) result += `\n\n${fields.note}`;
    setLoadingNote(false);
    setNewNote(result);
    setStreamingData([]);

    return result;
  };

  return {
    generateNote,
    newNote,
    setNewNote,
    loadingNote,
    filteredStreamingText,
  };
};
